/**
 * foldLearning — the pure derivation behind the learn board: what to work on
 * next, and the target that put it there.
 *
 * Ordering a trainee's targets and deciding one is overdue are NOT here: the
 * mentor board asks the same two questions of the same shape, and it cannot
 * import from this app. Both live beside SkillTarget in @frontend/hooks/useSkills.
 *
 * Pure and separately exported ON PURPOSE (foldAccess.ts precedent): inside a
 * component's useMemo the rule was only reachable through a browser E2E, which
 * is not in the per-PR CI set. Every rule below has a unit test in
 * foldLearning.test.ts.
 *
 * Nothing here invents state: a recommendation exists because a real open
 * target names the module.
 */

import type { SkillTarget } from '@frontend/hooks/useSkills'
import type { ProtocolSummary } from '@/api/endpoints/protocols'

import type { Recommendation } from './types'

export interface RecommendationInput {
  /** GET /protocols?status=published, quizzes only. */
  components: ProtocolSummary[]
  /** The learner's OPEN targets, already ordered (sortTargets). */
  openTargets: SkillTarget[]
}

/**
 * The published modules the learner's open targets name, in target order.
 *
 * A module two targets name is listed once, under the first. A module that left
 * the published catalogue is skipped: there is nothing left to run against.
 *
 * CURRICULA NEVER REACH HERE, and the board's catalogue read is what keeps
 * them out: it asks `composes_components=false`, so a programme over other
 * components is not in `components`. Enrolment is the supervisor's act.
 */
export function foldRecommendations({
  components,
  openTargets,
}: RecommendationInput): Recommendation[] {
  const byId = new Map(components.map((c) => [c.id, c]))
  const seen = new Set<string>()
  const recommendations: Recommendation[] = []
  // A target names a skill and the modules that lead to it; the module is what
  // the learner can start, and the SKILL is why she would.
  for (const t of openTargets) {
    for (const id of t.target.module_ids) {
      const component = byId.get(id)
      if (!component || seen.has(id)) continue
      seen.add(id)
      recommendations.push({ component, because: t.skillTitle })
    }
  }
  return recommendations
}
